// Blocking-interaction overlay: backdrop + adaptive card + per-type body.
// Web port of the mobile interaction UI (same engine, same UX decisions):
//  - pinned ✕ close (dismissed -> reopen chip), no progress bar
//  - MC: no pre-check tips; correct always marked after check; "Cevabı Göster"
//    reveals the explanation UNDER the correct option
//  - fill_blank / word_bank: per-blank ✓/✕ lighting + full-sentence reveal
//  - drag_drop: per-chip ✓/✕ lighting inside zones
//  - drag & drop AND click-to-place both work for the drag types
import React, { useMemo, useState } from 'react';
import { FormattedText } from './FormattedText';
import {
	validateAnswer,
	correctAnswerLabel,
	matchesFillBlankValue,
	normalizeCorrectBySlot,
	wordBankBlankCorrectness,
} from './validation';
import {
	parseFillBlankData,
	parseWordBankTemplate,
	getWordBankCorrectWords,
	getWordBankPool,
	shuffleIndexes,
} from './parsing';
import './interactions.css';

const isSheetType = (type) => type === 'drag_drop' || type === 'word_bank_fill';

function Shell({ sheet, footer, onClose, exampleImageUrl, bgImageUrl, children }) {
	const bg = (bgImageUrl || '').trim();
	const img = (exampleImageUrl || '').trim();
	return (
		<div className={`vi-card ${sheet ? 'vi-sheet' : ''}`}>
			{bg ? <div className="vi-card-bg" style={{ backgroundImage: `url(${bg})` }} /> : null}
			{onClose ? (
				<button type="button" className="vi-close" onClick={onClose}>
					✕
				</button>
			) : null}
			<div className="vi-body">
				{img ? <img className="vi-example-img" src={img} alt="Etkileşim görseli" /> : null}
				{children}
			</div>
			{footer ? <div className="vi-footer">{footer}</div> : null}
		</div>
	);
}

function ResultFooter({ revealed, revealText, showReveal, canRetry, onReveal, onRetry, onContinue }) {
	return (
		<div>
			<div className="vi-actions">
				{showReveal ? (
					<button type="button" className="vi-btn vi-btn-ghost" onClick={onReveal}>
						Cevabı Göster
					</button>
				) : null}
				{canRetry ? (
					<button type="button" className="vi-btn vi-btn-ghost" onClick={onRetry}>
						Tekrar Dene
					</button>
				) : null}
				<button type="button" className="vi-btn vi-btn-primary" onClick={onContinue}>
					Devam Et
				</button>
			</div>
			{revealed && revealText ? <div className="vi-reveal">{revealText}</div> : null}
		</div>
	);
}

function CheckFooter({ disabled, onCheck }) {
	return (
		<button type="button" className="vi-btn vi-btn-primary" style={{ width: '100%' }} disabled={disabled} onClick={onCheck}>
			Kontrol Et
		</button>
	);
}

/* ── true / false ── */
function TrueFalseCard({ interaction, onResult }) {
	const [selected, setSelected] = useState(null);
	const [checked, setChecked] = useState(false);
	const [revealed, setRevealed] = useState(false);
	const isCorrect = checked ? validateAnswer(interaction, selected) : false;
	// Summary granularity ("X/Y"): true/false is a single unit.
	const counts = { correct: isCorrect ? 1 : 0, total: 1 };

	const footer = !checked ? (
		<CheckFooter disabled={selected === null} onCheck={() => setChecked(true)} />
	) : (
		<ResultFooter
			revealed={revealed}
			revealText={`${correctAnswerLabel(interaction)}${interaction.revealText ? `\n\n${interaction.revealText}` : ''}`}
			showReveal={!!interaction.showSolution}
			canRetry={!!interaction.enableRetry}
			onReveal={() => setRevealed((r) => !r)}
			onRetry={() => {
				setChecked(false);
				setSelected(null);
				setRevealed(false);
			}}
			onContinue={() => onResult(isCorrect, false, counts)}
		/>
	);

	return (
		<Shell
			sheet={false}
			footer={footer}
			onClose={() => onResult(checked ? isCorrect : false, true, counts)}
			exampleImageUrl={interaction.exampleImageUrl}
			bgImageUrl={interaction.backgroundImageUrl}
		>
			<p className="vi-prompt"><FormattedText text={interaction.prompt} /></p>
			<div className="vi-tf-row">
				{[true, false].map((val) => {
					const sel = selected === val;
					const good = val === interaction.correct;
					// PM rule: color fills only ever mark YOUR pick (green right / red
					// wrong). On a wrong answer the correct option stays on a neutral
					// background — revealed only by its ✓ and the DOĞRU CEVAP tag.
					const cls = checked
						? sel
							? good
								? 'vi-correct'
								: 'vi-wrong'
							: ''
						: sel
							? 'vi-sel'
							: '';
					// After checking, every lit option says WHY it's lit — the labels
					// are themselves "Doğru/Yanlış", so color alone would be ambiguous.
					const lit = checked && (sel || good);
					const badge = checked ? (sel ? 'SENİN CEVABIN' : good ? 'DOĞRU CEVAP' : null) : null;
					return (
						<button
							type="button"
							key={String(val)}
							disabled={checked}
							className={`vi-tf-btn ${cls}`}
							onClick={() => setSelected(val)}
						>
							{lit ? (good ? '✓ ' : '✕ ') : ''}
							{val ? 'Doğru' : 'Yanlış'}
							{badge ? (
								<span className={`vi-tf-badge ${good ? 'vi-tf-badge-ok' : 'vi-tf-badge-bad'}`}>
									{badge}
								</span>
							) : null}
						</button>
					);
				})}
			</div>
		</Shell>
	);
}

/* ── multiple / single choice ── */
function MultipleChoiceCard({ interaction, onResult }) {
	const single = interaction.selectionMode === 'single';
	const [order] = useState(() => shuffleIndexes(interaction.options.length));
	const [selected, setSelected] = useState([]);
	const [checked, setChecked] = useState(false);
	const [revealed, setRevealed] = useState(false);
	const isCorrect = checked ? validateAnswer(interaction, selected) : false;
	// Summary granularity ("X/Y"): correctly picked options / correct options.
	const totalCorrectOptions = Math.max(1, interaction.options.filter((o) => o.isCorrect).length);
	const counts = {
		correct: selected.filter((i) => interaction.options[i]?.isCorrect).length,
		total: totalCorrectOptions,
	};

	const firstCorrectRendered = useMemo(
		() => order.find((i) => interaction.options[i]?.isCorrect),
		[order, interaction.options]
	);

	const toggle = (idx) => {
		if (checked) return;
		if (single) {
			setSelected([idx]);
			return;
		}
		setSelected((prev) => (prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]));
	};

	const footer = !checked ? (
		<CheckFooter disabled={selected.length === 0} onCheck={() => setChecked(true)} />
	) : (
		<ResultFooter
			revealed={revealed}
			showReveal={!!interaction.showSolution}
			canRetry={!!interaction.enableRetry}
			onReveal={() => setRevealed((r) => !r)}
			onRetry={() => {
				setChecked(false);
				setSelected([]);
				setRevealed(false);
			}}
			onContinue={() => onResult(isCorrect, false, counts)}
		/>
	);

	return (
		<Shell
			sheet={false}
			footer={footer}
			onClose={() => onResult(checked ? isCorrect : false, true, counts)}
			exampleImageUrl={interaction.exampleImageUrl}
			bgImageUrl={interaction.backgroundImageUrl}
		>
			<p className="vi-prompt"><FormattedText text={interaction.prompt} /></p>
			{order.map((optionIdx) => {
				const opt = interaction.options[optionIdx];
				const sel = selected.includes(optionIdx);
				const dot = checked ? (opt.isCorrect ? '✓' : sel ? '✕' : '○') : sel ? (single ? '◉' : '☑') : '○';
				const cls = checked
					? opt.isCorrect
						? 'vi-correct'
						: sel
							? 'vi-wrong'
							: ''
					: sel
						? 'vi-sel'
						: '';
				const fbText = sel ? opt.selectedFeedback : opt.unselectedFeedback;
				const feedback = checked && fbText && fbText.trim() ? fbText : '';
				return (
					<div key={optionIdx}>
						<button type="button" disabled={checked} className={`vi-mc-option ${cls}`} onClick={() => toggle(optionIdx)}>
							<span className="vi-mc-dot">{dot}</span>
							<span><FormattedText text={opt.text} /></span>
						</button>
						{feedback ? (
							<div className="vi-mc-feedback"><FormattedText text={feedback} /></div>
						) : null}
						{revealed && opt.isCorrect ? (
							<div className="vi-mc-revealbox">
								<div className="vi-mc-revealtitle">✓ Doğru cevap</div>
								{optionIdx === firstCorrectRendered && interaction.revealText && interaction.revealText.trim() ? (
									<div className="vi-mc-revealtext"><FormattedText text={interaction.revealText} /></div>
								) : null}
							</div>
						) : null}
					</div>
				);
			})}
		</Shell>
	);
}

/* ── fill in the blank (keyboard) ── */
function FillBlankCard({ interaction, onResult }) {
	const { parts, blanks } = useMemo(() => parseFillBlankData(interaction), [interaction]);
	const [values, setValues] = useState(() => blanks.map(() => ''));
	const [checked, setChecked] = useState(false);
	const [revealed, setRevealed] = useState(false);

	const isCorrect = checked ? validateAnswer(interaction, values) : false;
	const matches = blanks.map((acc, i) => matchesFillBlankValue(values[i] ?? '', acc));
	// Summary granularity ("2/3"): correctly filled blanks / total blanks.
	const counts = {
		correct: checked ? matches.filter(Boolean).length : 0,
		total: Math.max(1, blanks.length),
	};
	const canCheck = values.length > 0 && values.every((v) => v.trim().length > 0);
	const setVal = (i, v) => setValues((prev) => prev.map((x, j) => (j === i ? v : x)));

	const footer = !checked ? (
		<CheckFooter disabled={!canCheck} onCheck={() => setChecked(true)} />
	) : (
		<ResultFooter
			revealed={revealed}
			showReveal={!!interaction.showSolution}
			canRetry={!!interaction.enableRetry}
			onReveal={() => setRevealed((r) => !r)}
			onRetry={() => {
				setChecked(false);
				setValues(blanks.map(() => ''));
				setRevealed(false);
			}}
			onContinue={() => onResult(isCorrect, false, counts)}
		/>
	);

	return (
		<Shell
			sheet={false}
			footer={footer}
			onClose={() => onResult(checked ? isCorrect : false, true, counts)}
			exampleImageUrl={interaction.exampleImageUrl}
			bgImageUrl={interaction.backgroundImageUrl}
		>
			<p className="vi-prompt">Boş bırakılan yeri uygun kelime ile doldurun.</p>
			<div className="vi-fb-sentence">
				{parts.map((part, idx) =>
					part.type === 'text' ? (
						<span key={idx}>{part.value}</span>
					) : checked ? (
						<span key={idx} className={`vi-fb-chip ${matches[part.blankIndex] ? 'vi-correct' : 'vi-wrong'}`}>
							{values[part.blankIndex] || '-'} {matches[part.blankIndex] ? '✓' : '✕'}
						</span>
					) : (
						<input
							key={idx}
							className="vi-fb-input"
							value={values[part.blankIndex]}
							placeholder="cevap"
							autoCapitalize="none"
							autoCorrect="off"
							onChange={(e) => setVal(part.blankIndex, e.target.value)}
						/>
					)
				)}
			</div>
			{revealed ? (
				<div className="vi-reveal-box">
					<p className="vi-section-title">Doğru cümle</p>
					<div className="vi-reveal-sentence">
						{parts.map((part, idx) =>
							part.type === 'text' ? (
								<span key={idx}>{part.value}</span>
							) : (
								<span key={idx} className="vi-reveal-word">{blanks[part.blankIndex]?.[0] ?? ''}</span>
							)
						)}
					</div>
					{interaction.revealText && interaction.revealText.trim() ? (
						<div className="vi-reveal-note">{interaction.revealText}</div>
					) : null}
				</div>
			) : null}
		</Shell>
	);
}

/* ── word bank (drag the words into blanks) ── */
function WordBankFillCard({ interaction, onResult }) {
	const { parts } = useMemo(() => parseWordBankTemplate(interaction.sentenceTemplate), [interaction]);
	const correctWords = useMemo(() => getWordBankCorrectWords(interaction), [interaction]);
	const pool = useMemo(() => getWordBankPool(interaction), [interaction]);
	const blankCount = correctWords.length;

	const [assignments, setAssignments] = useState(() => Array.from({ length: blankCount }, () => null));
	const [selected, setSelected] = useState(null);
	const [checked, setChecked] = useState(false);
	const [revealed, setRevealed] = useState(false);

	const used = new Set(assignments.filter((x) => x !== null));
	const isCorrect = checked ? validateAnswer(interaction, assignments) : false;
	const canCheck = assignments.length > 0 && assignments.every((a) => a !== null);
	// Group-aware per-blank lighting: grouped blanks all light green when the
	// group's words match in any order (see wordBankBlankCorrectness).
	const blankResults = checked ? wordBankBlankCorrectness(interaction, assignments) : [];
	const blankIsCorrect = (i) => Boolean(blankResults[i]);
	// Summary granularity ("2/3"): correctly placed words / total blanks.
	const counts = {
		correct: checked
			? Array.from({ length: blankCount }, (_, i) => blankIsCorrect(i)).filter(Boolean).length
			: 0,
		total: Math.max(1, blankCount),
	};

	const assign = (blankIdx, poolIdx) => {
		if (checked || blankIdx < 0 || blankIdx >= blankCount) return;
		setAssignments((prev) => {
			const next = [...prev];
			const existing = next.findIndex((a) => a === poolIdx);
			if (existing >= 0) next[existing] = null;
			next[blankIdx] = poolIdx;
			return next;
		});
		setSelected(null);
	};
	const clearBlank = (blankIdx) => {
		if (!checked) setAssignments((prev) => prev.map((a, i) => (i === blankIdx ? null : a)));
	};

	const footer = !checked ? (
		<CheckFooter disabled={!canCheck} onCheck={() => setChecked(true)} />
	) : (
		<ResultFooter
			revealed={revealed}
			showReveal={!!interaction.showSolution}
			canRetry={!!interaction.enableRetry}
			onReveal={() => setRevealed((r) => !r)}
			onRetry={() => {
				setChecked(false);
				setRevealed(false);
				setSelected(null);
				setAssignments(Array.from({ length: blankCount }, () => null));
			}}
			onContinue={() => onResult(isCorrect, false, counts)}
		/>
	);

	return (
		<Shell
			sheet
			footer={footer}
			onClose={() => onResult(checked ? isCorrect : false, true, counts)}
			exampleImageUrl={interaction.exampleImageUrl}
			bgImageUrl={interaction.backgroundImageUrl}
		>
			{interaction.prompt ? <p className="vi-prompt"><FormattedText text={interaction.prompt} /></p> : null}

			<div className="vi-wb-sentence">
				{parts.map((part, idx) =>
					part.type === 'text' ? (
						<span key={idx}><FormattedText text={part.value} /></span>
					) : (
						<button
							type="button"
							key={idx}
							className={`vi-wb-blank ${
								checked
									? blankIsCorrect(part.blankIndex)
										? 'vi-correct'
										: 'vi-wrong'
									: assignments[part.blankIndex] !== null
										? 'vi-filled'
										: ''
							}`}
							onDragOver={(e) => e.preventDefault()}
							onDrop={(e) => {
								e.preventDefault();
								const wordIdx = Number(e.dataTransfer.getData('text/plain'));
								if (Number.isInteger(wordIdx)) assign(part.blankIndex, wordIdx);
							}}
							onClick={() => {
								if (checked) return;
								if (selected !== null) assign(part.blankIndex, selected);
								else if (assignments[part.blankIndex] !== null) clearBlank(part.blankIndex);
							}}
						>
							{assignments[part.blankIndex] !== null
								? `${pool[assignments[part.blankIndex]]}${checked ? (blankIsCorrect(part.blankIndex) ? ' ✓' : ' ✕') : ''}`
								: ''}
						</button>
					)
				)}
			</div>

			<p className="vi-section-title">Kelimeler</p>
			<div className="vi-chip-row">
				{pool.map((word, poolIdx) => {
					const isUsed = used.has(poolIdx);
					const sel = selected === poolIdx;
					return (
						<button
							type="button"
							key={`${word}_${poolIdx}`}
							className={`vi-chip ${sel ? 'vi-sel' : ''} ${isUsed || checked ? 'vi-used' : ''}`}
							draggable={!isUsed && !checked}
							disabled={isUsed || checked}
							onDragStart={(e) => e.dataTransfer.setData('text/plain', String(poolIdx))}
							onClick={() => !isUsed && !checked && setSelected((prev) => (prev === poolIdx ? null : poolIdx))}
						>
							<FormattedText text={word} />
						</button>
					);
				})}
			</div>

			{revealed ? (
				<div className="vi-reveal-box">
					<p className="vi-section-title">Doğru cümle</p>
					<div className="vi-reveal-sentence">
						{parts.map((part, idx) =>
							part.type === 'text' ? (
								<span key={idx}>{part.value}</span>
							) : (
								<span key={idx} className="vi-reveal-word">{correctWords[part.blankIndex]}</span>
							)
						)}
					</div>
				</div>
			) : null}
		</Shell>
	);
}

/* ── drag drop (items into zones) ── */
function DragDropCard({ interaction, onResult }) {
	const slotCount = interaction.slots.length;
	const [assignments, setAssignments] = useState(() => Array.from({ length: slotCount }, () => []));
	const [selected, setSelected] = useState(null);
	const [dragOverSlot, setDragOverSlot] = useState(null);
	const [checked, setChecked] = useState(false);
	const [revealed, setRevealed] = useState(false);

	const correctBySlot = useMemo(() => normalizeCorrectBySlot(interaction), [interaction]);
	const minRequired = correctBySlot.flat().length || 1;
	const used = new Set(assignments.flat());
	const canCheck = assignments.flat().length >= minRequired;
	const isCorrect = checked ? validateAnswer(interaction, { assignments }) : false;
	const chipIsCorrect = (slotIdx, itemIdx) => (correctBySlot[slotIdx] || []).includes(itemIdx);
	// Summary granularity ("2/3"): correctly placed chips / expected placements.
	const counts = {
		correct: checked
			? assignments.reduce(
					(sum, group, slotIdx) => sum + group.filter((i) => chipIsCorrect(slotIdx, i)).length,
					0
				)
			: 0,
		total: Math.max(1, minRequired),
	};

	const assignTo = (slotIdx, itemIdx) => {
		if (checked || slotIdx < 0 || slotIdx >= slotCount) return;
		setAssignments((prev) => {
			const next = prev.map((g) => [...g]);
			// Single-use: pull the item out of any other zone first. With
			// allowReuse the same item may stay in several zones at once.
			if (!interaction.allowReuse) {
				const at = next.findIndex((g) => g.includes(itemIdx));
				if (at >= 0) next[at] = next[at].filter((v) => v !== itemIdx);
			}
			if (!next[slotIdx].includes(itemIdx)) next[slotIdx].push(itemIdx);
			return next;
		});
		setSelected(null);
	};
	const removeItem = (slotIdx, itemIdx) => {
		if (checked) return;
		setAssignments((prev) => prev.map((g, i) => (i === slotIdx ? g.filter((v) => v !== itemIdx) : g)));
	};

	const footer = !checked ? (
		<CheckFooter disabled={!canCheck} onCheck={() => setChecked(true)} />
	) : (
		<ResultFooter
			revealed={revealed}
			revealText={correctAnswerLabel(interaction)}
			showReveal={!!interaction.showSolution}
			canRetry={!!interaction.enableRetry}
			onReveal={() => setRevealed((r) => !r)}
			onRetry={() => {
				setChecked(false);
				setRevealed(false);
				setSelected(null);
				setAssignments(Array.from({ length: slotCount }, () => []));
			}}
			onContinue={() => onResult(isCorrect, false, counts)}
		/>
	);

	return (
		<Shell
			sheet
			footer={footer}
			onClose={() => onResult(checked ? isCorrect : false, true, counts)}
			exampleImageUrl={interaction.exampleImageUrl}
			bgImageUrl={interaction.backgroundImageUrl}
		>
			<p className="vi-prompt"><FormattedText text={interaction.prompt || 'Sürükle Bırak Etkinliği'} /></p>

			<div className="vi-dd-grid">
				{interaction.slots.map((slot, slotIdx) => (
					<div className="vi-dd-zonewrap" key={`${slot}_${slotIdx}`}>
						<p className="vi-dd-zonelabel"><FormattedText text={slot} /></p>
						<div
							className={`vi-dd-zone ${assignments[slotIdx].length > 0 ? 'vi-filled' : ''} ${dragOverSlot === slotIdx ? 'vi-over' : ''}`}
							onDragOver={(e) => {
								e.preventDefault();
								setDragOverSlot(slotIdx);
							}}
							onDragLeave={() => setDragOverSlot(null)}
							onDrop={(e) => {
								e.preventDefault();
								setDragOverSlot(null);
								const itemIdx = Number(e.dataTransfer.getData('text/plain'));
								if (Number.isInteger(itemIdx)) assignTo(slotIdx, itemIdx);
							}}
							onClick={() => !checked && selected !== null && assignTo(slotIdx, selected)}
						>
							{assignments[slotIdx].length === 0 ? (
								<span className="vi-muted">{selected !== null ? 'Buraya eklemek için tıklayın' : 'Öğeyi buraya sürükleyin'}</span>
							) : (
								<div className="vi-chip-row">
									{assignments[slotIdx].map((itemIdx) => {
										const good = chipIsCorrect(slotIdx, itemIdx);
										return (
											<button
												type="button"
												key={itemIdx}
												className={`vi-chip-inslot ${checked ? (good ? 'vi-correct' : 'vi-wrong') : ''}`}
												disabled={checked}
												onClick={(e) => {
													e.stopPropagation();
													removeItem(slotIdx, itemIdx);
												}}
											>
												<FormattedText text={interaction.items[itemIdx]} />
												{checked ? (good ? ' ✓' : ' ✕') : ' ✕'}
											</button>
										);
									})}
								</div>
							)}
						</div>
					</div>
				))}
			</div>

			<p className="vi-section-title" style={{ marginTop: 16 }}>Öğeler</p>
			<div className="vi-chip-row">
				{interaction.items.map((item, itemIdx) => {
					const isUsed = !interaction.allowReuse && used.has(itemIdx);
					const sel = selected === itemIdx;
					return (
						<button
							type="button"
							key={`${item}_${itemIdx}`}
							className={`vi-chip ${sel ? 'vi-sel' : ''} ${isUsed || checked ? 'vi-used' : ''}`}
							draggable={!isUsed && !checked}
							disabled={isUsed || checked}
							onDragStart={(e) => e.dataTransfer.setData('text/plain', String(itemIdx))}
							onClick={() => !isUsed && !checked && setSelected((prev) => (prev === itemIdx ? null : itemIdx))}
						>
							<FormattedText text={item} />
						</button>
					);
				})}
			</div>
		</Shell>
	);
}

function UnsupportedCard({ interaction, onResult }) {
	return (
		<Shell sheet={false} footer={
			<button type="button" className="vi-btn vi-btn-primary" style={{ width: '100%' }} onClick={() => onResult(false, false, { correct: 0, total: 1 })}>
				Devam Et
			</button>
		} onClose={() => onResult(false, true, { correct: 0, total: 1 })}>
			<p className="vi-prompt"><FormattedText text={interaction.prompt || ''} /></p>
			<p className="vi-muted">Bu etkileşim türü desteklenmiyor.</p>
		</Shell>
	);
}

export function InteractionOverlay({ interaction, onResult }) {
	if (!interaction) return null;

	let card;
	switch (interaction.type) {
		case 'true_false':
			card = <TrueFalseCard interaction={interaction} onResult={onResult} />;
			break;
		case 'multiple_choice':
			card = <MultipleChoiceCard interaction={interaction} onResult={onResult} />;
			break;
		case 'fill_blank':
			card = <FillBlankCard interaction={interaction} onResult={onResult} />;
			break;
		case 'word_bank_fill':
			card = <WordBankFillCard interaction={interaction} onResult={onResult} />;
			break;
		case 'drag_drop':
			card = <DragDropCard interaction={interaction} onResult={onResult} />;
			break;
		default:
			card = <UnsupportedCard interaction={interaction} onResult={onResult} />;
	}

	return <div className="vi-backdrop">{card}</div>;
}

export { isSheetType };
